/**
 * STILLSUN post-processing — three r185 TSL nodes, no third-party deps.
 *
 * The whole chain, and there is deliberately nothing else in it:
 *
 *   pass(scene, camera, { samples: 4 })      // MSAA in the scene pass; the only AA
 *     -> bloom (low, additive, half-res input; `?bloom=0` removes it)
 *     -> grade + vignette (filmic contrast, violet lift, amber highlights, saturation)
 *     -> RenderPipeline's renderOutput (AgX tone map + sRGB)
 *
 * ## Why it is this short (2026-09-05 pivot)
 *
 * The target is 300 visible characters at 120 fps with `cpuMs` <= 4 ms (docs/PERF-BUDGET.md).
 * Everything the old chain carried was measured against a 17-rig first-person raid and priced
 * for a 4080: TAAU (history + velocity MRT), GTAO (half-res occlusion + normal MRT), screen
 * -space light shafts, an anamorphic streak, depth of field on aim, and film grain. Together
 * they were 6-9 ms of fixed per-frame cost and — via the MRT normal/velocity attachments —
 * a heavier shader variant on *every* material in the scene. All of it is gone, including the
 * kill switches that existed only to bisect it (`?taa`, `?gtao`, `?shafts`, `?streak`, `?dof`,
 * `?noise` for grain, `?rs`). 4x MSAA on the scene pass replaces TAAU/SMAA/FXAA outright: it
 * is a fixed-function resolve, it has no history to reseed, and it costs nothing on the CPU.
 *
 * MSAA is real here and not a hopeful option: `PassNode.setup()` writes
 * `renderTarget.samples` from the `samples` option (three.webgpu.js:40941) and the WebGPU
 * backend reads it into the pass's `sampleCount` (:34037) and into the render-context /
 * pipeline cache key (:33932). No SMAA fallback is needed.
 *
 * ## Dynamic resolution
 *
 * There is no DRS *sink* any more, and that is the fix rather than a regression of
 * WEBGPU-NOTES §2. The sink existed because TAAU wanted the scene pass to shrink while the
 * canvas stayed native — which is exactly the shape that left 4.7 MP of post running at output
 * resolution while the beauty dropped to 0.55x. Without it, DRS moves the canvas pixel ratio,
 * and every render target here (the scene pass, the bloom input) sizes itself from
 * `renderer.getDrawingBufferSize()` every frame (PassNode :40976, RTTNode :38385), so a DRS
 * step scales the *whole* chain including the output quad. Nothing runs at a resolution the
 * scene pass is not also running at.
 *
 * ## What is left of the old interface, and why
 *
 * `overlaysViewModel` and the `overlay` parameter are vestigial: first person is going away
 * with the view model, `game/viewmodel.ts` and `main.ts` are owned by another agent, and this
 * file is not allowed to change their call sites. `overlaysViewModel` is a constant `false`
 * (so main.ts keeps its own overlay path) and `overlay` is accepted and ignored. Both go when
 * the view model does. `setDimming` is a no-op for the same reason — the Dimming is out of the
 * game but main.ts still calls it.
 *
 * Kill switches: `?post=0` (whole chain), `?bloom=0`, `?grade=0`.
 */
import { Color, RenderPipeline } from 'three/webgpu';
import type { Camera, Node, Object3D, Vector3, WebGPURenderer } from 'three/webgpu';
import * as TSL from 'three/tsl';
import { Fn, float, mix, pass, saturation, screenUV, smoothstep, vec3, vec4 } from 'three/tsl';
import { bloom } from 'three/addons/tsl/display/BloomNode.js';
import { gfxStats, queryDisabled, type QualityTier } from './renderer.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyNode = any;
const convertToTexture: (n: AnyNode) => AnyNode = TSL.convertToTexture as unknown as (
  n: AnyNode,
) => AnyNode;

/**
 * A second scene/camera pair drawn over the world with its own depth — the weapon view model.
 *
 * @deprecated Accepted and ignored. Kept only so `main.ts` and `game/viewmodel.ts` (owned
 * elsewhere) keep compiling while first person is removed; delete with them.
 */
export interface PostOverlay {
  scene: Object3D;
  camera: Camera;
}

/** What `main.ts` holds. `render()` replaces the world's `renderer.render(scene, camera)`. */
export interface PostHandle {
  /** False when `?post=0` — `render()` is then a straight `renderer.render()`. */
  readonly enabled: boolean;
  /**
   * Always `false`. The chain no longer composites a view model; the caller keeps its own
   * overlay path. @deprecated — goes with first person.
   */
  readonly overlaysViewModel: boolean;
  /** Draws the world (through the chain, when enabled). Synchronous, like `renderer.render`. */
  render(): void;
  /**
   * Resizes the scene pass. The pass also re-reads the drawing buffer size in its own
   * `updateBefore()` each frame, so this is belt-and-braces for the dynamic-resolution path
   * rather than something the frame loop depends on.
   */
  setSize(width: number, height: number): void;
  /** @deprecated No-op. The Dimming is out of the game; `main.ts` still calls this. */
  setDimming(t: number): void;
  /** @deprecated No-op. Nothing in the chain is sun-relative any more (the shafts are gone). */
  setSunDirection(dir: Vector3, colour?: Color): void;
  /**
   * Builds every pipeline the chain needs, off the main-thread-blocking path where possible.
   * Call it behind the loading screen, next to `precompileScene`. See the note on the
   * implementation for why this is not optional.
   */
  compile(): Promise<void>;
  /**
   * Just the scene-material half of `compile()`, for a map swap: the new map's materials have
   * to be built against the pass's render target as well as the canvas. Pass it to
   * `precompileScene`'s `extra` hook so it runs with the render loop parked.
   */
  compileScene(): Promise<void>;
  dispose(): void;
}

// --- scene pass ----------------------------------------------------------------------
/**
 * MSAA sample count on the scene pass — the only anti-aliasing in the build. 4x is the WebGPU
 * sweet spot (2x barely helps a stylised silhouette; 8x is not guaranteed to be supported) and
 * the resolve is fixed-function, so it never touches the frame's CPU budget. The backing store
 * is capped at a 2560 long edge in `renderer.ts`, which is what keeps the resolve bandwidth
 * bounded on a high-DPI display.
 */
const MSAA_SAMPLES = 4;

// --- grade constants ----------------------------------------------------------------
// `new Color(hex)` converts sRGB hex into the renderer's working (linear-sRGB) space, which
// is the space this whole chain runs in — everything here is pre-tone-map, pre-sRGB.

/** Shadows are lifted toward this, additively. The Still's cold half. */
const SHADOW_VIOLET = new Color(0x2a2140);
/** Highlights are pushed toward this, multiplicatively. The Still's warm half. */
const HIGHLIGHT_AMBER = new Color(0xffc27a);

/** How much of `SHADOW_VIOLET` lands in the darkest pixels. */
const SHADOW_LIFT = 0.4;
/** How far highlights are pushed toward amber at full weight. */
const HIGHLIGHT_WARMTH = 0.28;
/**
 * Filmic contrast: a gentle S-curve around scene-linear mid grey, applied *before* AgX. AgX's
 * own curve is deliberately low-contrast so it never clips; this puts a print stock's shoulder
 * and toe back without touching the tone mapper. 0 = off. Luminance-only, so hues stay put.
 */
const FILMIC_CONTRAST = 0.18;
const FILMIC_PIVOT = 0.18;
const SATURATION = 1.05;
/** Vignette strength, and the radius (0 = centre, 1 = corner) where it starts. */
const VIGNETTE = 0.35;
const VIGNETTE_START = 0.3;

// --- bloom ---------------------------------------------------------------------------
/**
 * Scene-linear luminance above which a pixel blooms: lantern strips, emissive seams, the sun
 * disc. Raised from the old chain's 0.85 because the stylised materials are unlit
 * `MeshBasicNodeMaterial`s whose albedo sits much closer to its final on-screen value — 0.85
 * would have bloomed ordinary bright cloth.
 */
const BLOOM_THRESHOLD = 1;
/** Low by brief. Bloom is a hint that something is emissive, not a look. */
const BLOOM_STRENGTH = 0.22;
const BLOOM_RADIUS = 0.6;
/**
 * Ceiling on what one pixel may contribute, in scene-linear units. Without it the sun and the
 * sky sit far above everything else on screen, and blurring something that bright across five
 * mip levels lifts the entire frame. The old chain carried a second, depth-aware ceiling for
 * sky pixels; that needed the pass's depth texture, which under MSAA is a multisampled
 * attachment and not a thing to sample casually — one flat clamp instead, low enough that the
 * horizon behaves and high enough that a lantern still blooms.
 */
const BLOOM_CLAMP = 2.5;
/** The bloom's own input target, relative to the drawing buffer. Its five mips start here. */
const BLOOM_INPUT_SCALE = 0.5;

/**
 * Luminance-preserving tint direction for a colour: `c * tintOf(x)` shifts hue without
 * dragging the overall exposure with it. Precomputed on the CPU — these are constants.
 */
function tintOf(c: Color): [number, number, number] {
  const lum = Math.max(1e-4, 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b);
  return [c.r / lum, c.g / lum, c.b / lum];
}

const AMBER_TINT = tintOf(HIGHLIGHT_AMBER);

/**
 * Builds the post chain for `scene`/`camera`.
 *
 * Safe to call before anything has rendered; nothing here touches the GPU until `compile()`
 * or the first `render()`.
 *
 * @param tier    Kept in the signature for the caller's sake; the chain is now identical on
 *                every tier. Tier-specific rendering lives in `renderer.ts` and `shadows.ts`.
 * @param overlay @deprecated Accepted and ignored — see the module header.
 */
export function createPost(
  renderer: WebGPURenderer,
  scene: Object3D,
  camera: Camera,
  tier: QualityTier,
  overlay?: PostOverlay,
): PostHandle {
  void tier;
  void overlay;
  const enabled = !queryDisabled('post');
  gfxStats.post = enabled;

  if (!enabled) {
    console.info('[stillsun] post-processing: off (?post=0)');
    gfxStats.aaMode = gfxStats.aa ? 'msaa' : 'none';
    return {
      enabled: false,
      overlaysViewModel: false,
      render() {
        renderer.render(scene, camera);
      },
      setSize() {
        /* nothing to resize */
      },
      setDimming() {
        /* no chain to dim */
      },
      setSunDirection() {
        /* nothing sun-relative in the chain */
      },
      async compile() {
        /* the canvas compile in renderer.ts covers this path */
      },
      async compileScene() {
        /* as above */
      },
      dispose() {
        /* nothing to dispose */
      },
    };
  }

  // Bisection switches for the look, not for perf: the chain has no optional cost left worth
  // a flag except the bloom.
  const wantsBloom = !queryDisabled('bloom');
  const wantsGrade = !queryDisabled('grade');

  // --- scene pass --------------------------------------------------------------------
  // No MRT. The normal and velocity attachments existed only to feed GTAO and TAAU, and they
  // changed every material's shader variant to carry them.
  const scenePass = pass(scene, camera, { samples: MSAA_SAMPLES });

  // `@types/three` declares several of the display addons as a bare `TempNode` even though
  // they construct themselves with `'vec4'`. The chain is therefore held as an explicit
  // `Node<'vec4'>` and those nodes are cast back to what the runtime already knows they are.
  let node: Node<'vec4'> = scenePass.getTextureNode('output');

  // --- bloom -------------------------------------------------------------------------
  // Additive, the way three's own examples compose it: the node returns only the glow. The
  // clamp and the high pass happen here (into a half-res RTT the blurs sample) rather than
  // inside `BloomNode`, so the NaN guard and the ceiling sit in one place. A single non-finite
  // texel would otherwise poison every mip it touches — `min()` does not clamp NaN, and
  // `x == x` is false only for NaN.
  if (wantsBloom) {
    const raw = node.rgb;
    const finite = raw.x.equal(raw.x).and(raw.y.equal(raw.y)).and(raw.z.equal(raw.z));
    const safe = (TSL.select as AnyNode)(finite, raw.max(0), vec3(0));
    const bloomIn: AnyNode = convertToTexture(
      vec4(safe.min(vec3(BLOOM_CLAMP)).sub(BLOOM_THRESHOLD).max(0), float(1)),
    );
    bloomIn.setResolutionScale(BLOOM_INPUT_SCALE);
    node = vec4(node.add(bloom(bloomIn, BLOOM_STRENGTH, BLOOM_RADIUS, 0)));
  }

  // --- grade + vignette --------------------------------------------------------------
  const graded = node;
  const grade = Fn(() => {
    const c = graded.rgb.toVar();

    // Filmic contrast, luminance-only: an S-curve through mid grey in log space, so the toe
    // deepens the violet shadows and the shoulder rolls the amber highlights off, without
    // shifting hue. `x * pow(x / pivot, k)` is a power curve pivoting at `pivot`.
    const lum0 = c.r.mul(0.2126).add(c.g.mul(0.7152)).add(c.b.mul(0.0722)).max(1e-4);
    const curved = lum0.mul(lum0.div(FILMIC_PIVOT).pow(FILMIC_CONTRAST));
    c.mulAssign(curved.div(lum0));

    const lum = c.r.mul(0.2126).add(c.g.mul(0.7152)).add(c.b.mul(0.0722));

    // Shadows toward violet: additive, so it lifts the floor rather than muddying midtones.
    // `smoothstep` is `(low, high, x)` and a descending pair is undefined in WGSL/GLSL, hence
    // the ascending edges plus a `oneMinus` rather than `smoothstep(0.25, 0, lum)`.
    const shadowWeight = smoothstep(0, 0.25, lum).oneMinus();
    c.addAssign(
      vec3(SHADOW_VIOLET.r, SHADOW_VIOLET.g, SHADOW_VIOLET.b).mul(
        shadowWeight.mul(float(SHADOW_LIFT)),
      ),
    );

    // Highlights toward amber: multiplicative and luminance-preserving, so bright edges turn
    // gold instead of just getting brighter.
    const highlightWeight = smoothstep(0.35, 1, lum);
    const amber = vec3(AMBER_TINT[0], AMBER_TINT[1], AMBER_TINT[2]);
    c.assign(mix(c, c.mul(amber), highlightWeight.mul(float(HIGHLIGHT_WARMTH))));

    c.assign(saturation(c, float(SATURATION)));

    // Vignette. `d` is 0 at the centre and ~1 in the corners; squaring the smoothstep keeps
    // the middle of the screen flat and pushes the falloff out to the edges.
    const d = screenUV.sub(0.5).length().mul(float(Math.SQRT2));
    const falloff = smoothstep(float(VIGNETTE_START), 1, d);
    c.mulAssign(float(1).sub(falloff.mul(falloff).mul(float(VIGNETTE))));

    return vec4(c, graded.a);
  });

  if (wantsGrade) node = vec4(grade());

  // --- output ------------------------------------------------------------------------
  // No spatial AA node: the scene pass is multisampled and resolves before anything here
  // samples it. `RenderPipeline` applies AgX + sRGB after the chain.
  const pipeline = new RenderPipeline(renderer);
  pipeline.outputNode = node;
  pipeline.needsUpdate = true;
  gfxStats.aaMode = 'msaa';
  gfxStats.renderScale = 1;

  // --- render-context depth mapping -----------------------------------------------------
  // three keys a render context — and through it every pipeline — on the *render call depth*,
  // and `compileAsync` always compiles at depth 0. The scene pass really draws two calls deep
  // inside `pipeline.render()` and its shadow cascade three, so every pipeline a precompile
  // built was one the frame never used, and the frame then compiled its own synchronously on
  // first draw — the "shots do not register" hitch. Measured 2026-09-05: a 5 s parked compile
  // into contexts 2/3, followed by 2.3 s of synchronous compiles into contexts 8/9 for the same
  // materials. The wrapper records the depth each render target is really drawn at and
  // substitutes it while `compileScene` runs, so the async compile lands in the right cache.
  type ContextsGet = (rt: unknown, mrt: unknown, depth?: number) => unknown;
  const contexts = (renderer as unknown as { _renderContexts: { get: ContextsGet } })
    ._renderContexts;
  const depthByTarget = new Map<unknown, number>();
  let compiling = false;
  const origGet = contexts.get.bind(contexts);
  contexts.get = (rt, mrt, depth = 0) => {
    if (compiling) depth = depthByTarget.get(rt) ?? depth;
    else depthByTarget.set(rt, depth);
    return origGet(rt, mrt, depth);
  };

  console.info(
    `[stillsun] post-processing: on · msaa x${String(MSAA_SAMPLES)}` +
      `${wantsBloom ? ' · bloom' : ''}${wantsGrade ? ' · grade · vignette' : ''}`,
  );

  return {
    enabled: true,
    overlaysViewModel: false,

    render() {
      pipeline.render();
    },

    setSize(width, height) {
      scenePass.setSize(width, height);
    },

    setDimming() {
      /* no-op: the Dimming is out of the game. See the module header. */
    },

    setSunDirection() {
      /* no-op: nothing in the chain is sun-relative any more. */
    },

    /**
     * Two compiles, in this order, and both are load-bearing.
     *
     * 1. One `pipeline.render()` builds the effect quads themselves (bloom's five blur levels
     *    and the big output shader). There is no async path for those — they are internal
     *    `QuadMesh` materials — so this pays for them here, behind the loading screen, instead
     *    of on the first frame of a raid. It also compiles, synchronously, every scene material
     *    the frame can see, and records the call depth of every render target for the wrapper
     *    above.
     * 2. `compileScene` then walks the graph without the frustum — the parked warm-up bodies
     *    that `precompileScene` attaches, anything behind the camera — against the pass's
     *    render target, sample count and now-known depth, so those land in the same cache the
     *    frame reads. The canvas compile in `renderer.ts` is skipped while the chain is on:
     *    nothing but the output quad ever draws to the canvas.
     *
     * **The order is load-bearing for MSAA.** `PassNode.setup()` is what writes
     * `renderTarget.samples = 4`, and `samples` is part of the render-context and pipeline
     * cache key. `pipeline.render()` fires `setup()`, so by the time `compileScene()` targets
     * the pass the target really is 4x. Compile first and every material lands in a
     * single-sample context and recompiles on the first real frame — a hitch the
     * `depthByTarget` wrapper below cannot catch, because it keys on target identity, not on
     * sample count. Gate: `perf-probe.mjs`'s "new pipelines during samples" must read 0.
     */
    async compile() {
      const t = performance.now();
      try {
        pipeline.render();
        await this.compileScene();
      } catch (err) {
        console.warn('[stillsun] post precompile failed; compiling on demand', err);
      }
      console.info(`[stillsun] post chain compiled in ${Math.round(performance.now() - t)} ms`);
    },

    async compileScene() {
      compiling = true;
      try {
        await scenePass.compileAsync(renderer);
      } finally {
        compiling = false;
      }
    },

    dispose() {
      pipeline.dispose();
      scenePass.dispose();
    },
  };
}
